<template>
  <HeaderComponent />
  
  <!-- Breadcrumb -->
  <section class="w-full mt-[70px] bg-gray-50 border-b border-gray-200 py-4">
    <div class="container lg:max-w-[1200px] mx-auto px-[20px]">
      <nav class="flex" aria-label="Breadcrumb">
        <ol class="inline-flex items-center space-x-2 text-sm">
          <li class="inline-flex items-center">
            <router-link to="/" class="text-gray-500 hover:text-orange-600 transition-colors">Accueil</router-link>
          </li>
          <li>
            <div class="flex items-center">
              <svg class="w-4 h-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
              </svg>
              <router-link to="/boutique" class="text-gray-500 hover:text-orange-600 transition-colors">Boutique</router-link>
            </div>
          </li>
          <li>
            <div class="flex items-center">
              <svg class="w-4 h-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
              </svg>
              <span class="text-orange-600 font-medium">{{ produit.name }}</span>
            </div>
          </li>
        </ol>
      </nav>
    </div>
  </section>

  <!-- Notifications -->
  <div class="fixed top-[80px] left-[50%] z-[100] translate-x-[-50%] w-[80%] max-w-[400px] flex flex-col">
    <div v-if="errorMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-red-700 my-[10px] font-bold text-center p-[20px]">
      {{ errorMessage }}
    </div>
    <div v-if="successMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-green-700 my-[10px] font-bold text-center p-[20px]">
      {{ successMessage }}
    </div>
  </div>

  <!-- Main Content -->
  <div class="container lg:max-w-[1200px] mx-auto px-[20px] py-8">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="fixed inset-0 bg-white bg-opacity-80 z-10 flex items-center justify-center">
      <div class="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <div class="flex flex-col lg:flex-row gap-8">
      <!-- Product Gallery -->
      <div class="lg:w-1/2">
        <!-- Main Image -->
        <div class="bg-gray-100 rounded-xl overflow-hidden mb-4 h-96 flex items-center justify-center">
          <img 
            :src="selectedImage || defaultImage" 
            :alt="produit.name" 
            class="object-contain w-full h-full p-8 transition-opacity duration-300"
            @error="handleImageError"
          >
        </div>
        
        <!-- Thumbnails -->
        <div v-if="produit.photos && produit.photos.length > 1" class="flex gap-3 overflow-x-auto py-2">
          <div 
            v-for="(image, index) in produit.photos" 
            :key="index" 
            @click="selectedImage = image"
            class="flex-shrink-0 w-20 h-20 bg-gray-100 rounded-lg overflow-hidden cursor-pointer border-2 transition-all"
            :class="{
              'border-orange-500': selectedImage === image,
              'border-transparent': selectedImage !== image,
              'hover:border-orange-300': selectedImage !== image
            }"
          >
            <img 
              :src="image" 
              :alt="'Thumbnail ' + (index + 1)" 
              class="object-cover w-full h-full"
              @error="handleThumbnailError($event, index)"
            >
          </div>
        </div>
      </div>

      <!-- Product Info & Checkout -->
      <div class="lg:w-1/2">
        <!-- Product Header -->
        <div class="mb-6">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ produit.name }}</h1>
          <div class="flex items-center mb-4">
            <div class="flex text-orange-400 mr-3">
              <i class="fas fa-star"></i>
              <i class="fas fa-star ml-1"></i>
              <i class="fas fa-star ml-1"></i>
              <i class="fas fa-star ml-1"></i>
              <i class="fas fa-star-half-alt ml-1"></i>
            </div>
            <span class="text-gray-400 mx-2">|</span>
            <span class="text-green-600 font-medium flex items-center">
              <i class="fas fa-check-circle mr-1"></i> En stock
            </span>
          </div>
        </div>

        <!-- Product Description -->
        <div class="mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Description</h3>
          <p class="text-gray-600">{{ produit.description || 'Aucune description disponible' }}</p>
        </div>

        <!-- Variant Selection -->
        <div v-if="productVariants.length > 1" class="mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Options disponibles</h3>
          <div class="space-y-3">
            <div 
              v-for="variant in productVariants" 
              :key="variant.id"
              @click="selectedVariant = variant"
              :class="{
                'ring-2 ring-orange-500 bg-orange-50': selectedVariant.id === variant.id,
                'bg-gray-50 hover:bg-gray-100': selectedVariant.id !== variant.id
              }"
              class="p-4 rounded-lg cursor-pointer transition-all"
            >
              <div class="flex justify-between items-center">
                <div>
                  <p class="font-bold text-gray-900 text-lg">{{ formatPrice(variant.price) }}</p>
                  <div v-if="variant.originalPrice !== variant.price" class="flex items-center">
                    <span class="text-gray-400 text-sm line-through mr-2">{{ formatPrice(variant.originalPrice) }}</span>
                    <span class="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">
                      Économisez {{ formatPrice(variant.originalPrice - variant.price) }}
                    </span>
                  </div>
                </div>
                <div v-if="variant.capacity || variant.color" class="text-right">
                  <p v-if="variant.capacity" class="text-gray-600 text-sm">{{ variant.capacity }}</p>
                  <p v-if="variant.color" class="text-gray-600 text-sm">{{ variant.color }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Quantity Selector -->
        <div class="mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Quantité</h3>
          <div class="flex items-center">
            <button 
              @click="quantity > 1 ? quantity-- : null"
              :class="{'opacity-50 cursor-not-allowed': quantity === 1}"
              class="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-l-lg bg-gray-100 text-xl"
            >
              -
            </button>
            <div class="w-16 h-10 flex items-center justify-center border-t border-b border-gray-300">
              {{ quantity }}
            </div>
            <button 
              @click="quantity++"
              class="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-r-lg bg-gray-100 text-xl"
            >
              +
            </button>
          </div>
        </div>

        <!-- Customer Information -->
        <div class="mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Informations personnelles</h3>
          <div class="space-y-4">
            <div class="w-full relative md:flex justify-between items-center">
              <div class="md:w-[48%] w-full my-[15px] relative">
                <input v-model="nom" type="text" class="w-full p-[10px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100" placeholder="Nom" required>
              </div>
              <div class="md:w-[48%] w-full my-[15px] relative">
                <input v-model="prenoms" type="text" class="w-full p-[10px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100" placeholder="Prénoms" required>
              </div>
            </div>
            
            <div class="w-full relative my-[15px]">
              <input v-model="whatsapp" type="tel" class="w-full p-[10px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100" placeholder="Numéro WhatsApp" required>
            </div>
            
            <div class="w-full relative my-[15px]">
              <input v-model="email" type="email" class="w-full p-[10px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100" placeholder="Email">
            </div>
          </div>
        </div>

        <!-- Order Summary - Exactement comme dans votre panier -->
        <div class="bg-white rounded-[10px] py-[20px]">
          
          <!-- Total -->
          <div class="flex justify-between items-center py-[15px] border-t border-gray-200">
            <p class="text-sm font-bold text-gray-800">Total:</p>
            <p class="text-xl font-bold text-orange-500">{{ formatPrice(selectedVariant.price * quantity) }}</p>
          </div>
          
          <!-- Livraison gratuite - Style identique à votre panier -->
          <div class="w-full p-[10px] py-[15px] rounded-[5px] bg-orange-100 mt-[15px]">
            <div class="flex justify-between items-center">
              <div>
                <p class="text-[10px] font-bold text-gray-500">LIVRAISON GRATUITE</p>
                <p class="text-xs font-semibold text-gray-700">Calavi - Parakou</p>
              </div>
              <p class="text-sm font-bold text-orange-600">0.00 FCFA</p>
            </div>
          </div>
          
          <!-- Order Button -->
          <button 
            @click="confirmOrder"
            class="w-full mt-[20px] h-[45px] flex justify-center items-center p-[10px] rounded-[5px] bg-green-500 hover:bg-green-600 text-white text-sm font-bold text-center transition-colors"
          >
            Valider la commande
          </button>
          
          <p class="mt-3 text-xs text-gray-500 text-center">
            Un conseiller vous contactera pour finaliser votre commande
          </p>
        </div>
      </div>
    </div>
  </div>

  <FooterComponent />
</template>

<script setup>
import HeaderComponent from '../components/HeaderComponent.vue'
import FooterComponent from '../components/FooterComponent.vue'
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from "axios"

const router = useRouter()
const produit = ref({})
const isLoading = ref(true)
const selectedImage = ref('')
const defaultImage = '/img/default.png'
const brokenImages = ref([])
const quantity = ref(1)

// Form data - Identique à votre panier
const nom = ref('')
const prenoms = ref('')
const whatsapp = ref('')
const email = ref('')
const adresse = ref('A demander')
const errorMessage = ref('')
const successMessage = ref('')

// Product variants
const productVariants = ref([])
const selectedVariant = ref({})

const fetchProduitById = async () => {
  try {
    const response = await axios.get(`/api/lots/${router.currentRoute.value.params.id}`)
    produit.value = response.data
    
    if (produit.value.photos && produit.value.photos.length) {
      selectedImage.value = produit.value.photos[0]
    } else {
      selectedImage.value = defaultImage
    }
    
    // Prepare product variants
    productVariants.value = produit.value.produits
      .filter(prod => prod.isOnline)
      .map(prod => ({
        id: prod._id,
        imei1: prod.imei1,
        imei2: prod.imei2,
        price: prod.prix_reduit || prod.prix,
        originalPrice: prod.prix,
        capacity: prod.capacities?.[0] || null,
        color: prod.colors?.[0] || null,
      }))

    // Set default selected variant
    if (productVariants.value.length > 0) {
      selectedVariant.value = productVariants.value[0]
    }
  } catch (err) {
    console.error('Erreur lors du chargement du produit:', err)
  } finally {
    isLoading.value = false
  }
}

const handleImageError = (event) => {
  event.target.src = defaultImage
  if (produit.value.photos && produit.value.photos.includes(event.target.src)) {
    brokenImages.value.push(event.target.src)
    tryNextImage()
  }
}

const handleThumbnailError = (event, index) => {
  event.target.src = defaultImage
  brokenImages.value.push(produit.value.photos[index])
}

const tryNextImage = () => {
  if (!produit.value.photos) return
  
  const currentIndex = produit.value.photos.indexOf(selectedImage.value)
  if (currentIndex === -1) return
  
  const nextIndex = (currentIndex + 1) % produit.value.photos.length
  if (!brokenImages.value.includes(produit.value.photos[nextIndex])) {
    selectedImage.value = produit.value.photos[nextIndex]
  }
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA'
}

// Confirm order with EXACT same data structure as your cart
const confirmOrder = async () => {
  if (!nom.value || !whatsapp.value || !adresse.value) {
    errorMessage.value = 'Veuillez remplir tous les champs obligatoires'
    setTimeout(() => errorMessage.value = '', 3000)
    return
  }

  isLoading.value = true

  try {
    // Same structure as your cart example
    const saleData = {
      client: {
        nom: nom.value,
        prenoms: prenoms.value,
        email: email.value,
        whatsapp: whatsapp.value,
        adresse: adresse.value
      },
      produits: [{
        _id: produit.value._id,
        lotName: produit.value.name,
        lotCategorie: produit.value.categorie,
        photos: selectedImage.value,
        imei1: selectedVariant.value.imei1,
        imei2: selectedVariant.value.imei2,
        capacities: selectedVariant.value.capacity ? [selectedVariant.value.capacity] : [],
        colors: selectedVariant.value.color ? [selectedVariant.value.color] : [],
        prix: selectedVariant.value.price,
        prix_reduit: selectedVariant.value.originalPrice,
        quantity: quantity.value,
        stotal: selectedVariant.value.price * quantity.value
      }],
      total: selectedVariant.value.price * quantity.value
    }

    // Same endpoint as your cart
    await axios.post('/api/online', saleData)
    
    successMessage.value = 'Votre commande a été enregistrée avec succès!'
    setTimeout(() => successMessage.value = '', 3000)
    
    // Reset form
    nom.value = ''
    prenoms.value = ''
    whatsapp.value = ''
    email.value = ''
    adresse.value = ''
    quantity.value = 1

  } catch (error) {
    errorMessage.value = 'Une erreur est survenue lors de la confirmation de votre commande'
    setTimeout(() => errorMessage.value = '', 3000)
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchProduitById)
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.shadow-4xl {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
}

.parent {
  scrollbar-width: thin;
  scrollbar-color: #f97316 #f3f4f6;
}

.parent::-webkit-scrollbar {
  width: 6px;
}

.parent::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.parent::-webkit-scrollbar-thumb {
  background-color: #f97316;
  border-radius: 20px;
}
</style>